import type { DetectedFace } from '../types';
import { computeEar, LEFT_EYE_EAR, RIGHT_EYE_EAR } from './eyeOpenness';

/** Self-hosted assets (wasm synced by scripts/sync-mediapipe-assets.mjs; model committed). */
const WASM_BASE_PATH = '/mediapipe-wasm';
const MODEL_PATH = '/models/face_landmarker.task';

/** Detect up to this many faces — we need >1 to surface 'multiple-faces'. */
const MAX_FACES = 2;

/**
 * FaceMesh keypoint indices for the compact landmark set the framing rules
 * use (eyes above nose above mouth, eye separation, eye line). Eye "centers"
 * are the midpoints of the eye corners; mouth is the inner-lip center.
 */
const RIGHT_EYE_CORNERS = [33, 133];
const LEFT_EYE_CORNERS = [362, 263];
const NOSE_TIP = 1;
const MOUTH_INNER = [13, 14];

export interface VideoFaceDetector {
  /** Detect faces on the current video frame. `timestampMs` must be monotonic. */
  detect(video: HTMLVideoElement, timestampMs: number): DetectedFace[];
  close(): void;
}

/**
 * Thin wrapper around MediaPipe's Face Landmarker (478-point FaceMesh +
 * eyeBlink blendshapes) so the rest of the feature only sees a bounding box,
 * a few keypoints, and eye-openness signals. Replaces the BlazeFace detector:
 * the mesh gives eyelid geometry (EAR) and the ML eyeBlink score, which are
 * skin-tone / lighting independent — unlike the old pixel occlusion gates.
 */
export async function createVideoFaceDetector(): Promise<VideoFaceDetector> {
  // Dynamic import keeps the MediaPipe bundle out of the entry chunk; it loads
  // in parallel with camera startup.
  const { FaceLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');
  const fileset = await FilesetResolver.forVisionTasks(WASM_BASE_PATH);
  const landmarker = await FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: MODEL_PATH },
    runningMode: 'VIDEO',
    numFaces: MAX_FACES,
    outputFaceBlendshapes: true,
  });

  return {
    detect(video, timestampMs) {
      const result = landmarker.detectForVideo(video, timestampMs);
      const frameW = video.videoWidth;
      const frameH = video.videoHeight;
      return result.faceLandmarks.map((mesh, i) => {
        const px = (idx: number) => ({ x: mesh[idx]!.x * frameW, y: mesh[idx]!.y * frameH });
        const mid = (idxs: number[]) => {
          const pts = idxs.map(px);
          return {
            x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
            y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
          };
        };

        // Bounding box from the mesh extents (source-frame pixels).
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        for (const p of mesh) {
          if (p.x < minX) minX = p.x;
          if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y;
          if (p.y > maxY) maxY = p.y;
        }

        // eyeBlink blendshape scores (0–1, 1 = closed). Missing → 0 (not blinking).
        const cats = result.faceBlendshapes[i]?.categories ?? [];
        const blink = (name: string) => cats.find((c) => c.categoryName === name)?.score ?? 0;

        return {
          x: minX * frameW,
          y: minY * frameH,
          width: (maxX - minX) * frameW,
          height: (maxY - minY) * frameH,
          landmarks: {
            rightEye: mid(RIGHT_EYE_CORNERS),
            leftEye: mid(LEFT_EYE_CORNERS),
            noseTip: px(NOSE_TIP),
            mouth: mid(MOUTH_INNER),
          },
          eyeOpenness: {
            earLeft: computeEar(LEFT_EYE_EAR.map(px)),
            earRight: computeEar(RIGHT_EYE_EAR.map(px)),
            blinkLeft: blink('eyeBlinkLeft'),
            blinkRight: blink('eyeBlinkRight'),
          },
        } satisfies DetectedFace;
      });
    },
    close() {
      landmarker.close();
    },
  };
}
